import type { FC, KeyboardEventHandler } from "react";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { BirthFormInputs } from "@ui/eva-components/SoftAgeVerification/SoftAgeVerification";

import type { BirthDateFormInputsFormat } from "../../BirthDateFormInputs";
import { useMinimumAgeValidator } from "../../useMinimumAgeValidator";
import { DAY_INPUT_MAX_LENGTH, DayInput } from "../DayInput/DayInput";
import { MONTH_INPUT_MAX_LENGTH, MonthInput } from "../MonthInput/MonthInput";
import { YEAR_INPUT_MAX_LENGTH, YearInput } from "../YearInput/YearInput";

type BirthDateInputsProps = {
  dayPlaceholder: string;
  monthPlaceholder: string;
  yearPlaceholder: string;
  dayAriaLabel: string;
  monthAriaLabel: string;
  yearAriaLabel: string;
  minimumAge: number;
  format: BirthDateFormInputsFormat;
};

type InputOrder = {
  element: HTMLInputElement | null;
  maxLength: number;
};

const reverseOrderByFormat = (format: BirthDateFormInputsFormat, orders: InputOrder[]) => {
  if (format === "yyyy/mm/dd") {
    return orders.slice().reverse();
  }
  return orders;
};

export const BirthDateInputs: FC<BirthDateInputsProps> = ({
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
  dayAriaLabel,
  monthAriaLabel,
  yearAriaLabel,
  minimumAge,
  format,
}) => {
  const dayInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const { watch, setError, clearErrors } = useFormContext<BirthFormInputs>();
  const validateMinimumAge = useMinimumAgeValidator();
  watch((values) => {
    const year = Number(values?.year);
    const month = format === "yyyy" ? 1 : Number(values?.month);
    const day = format === "yyyy" ? 1 : Number(values?.day);

    validateMinimumAge({
      year,
      month,
      day,
      minimumAge,
      setMinimumAgeError: () => setError("minimumAge", {}),
      clearMinimumAgeError: () => clearErrors("minimumAge"),
    });
  });
  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const orderOfInputs: InputOrder[] = reverseOrderByFormat(format, [
      { element: dayInputRef.current, maxLength: DAY_INPUT_MAX_LENGTH },
      { element: monthInputRef.current, maxLength: MONTH_INPUT_MAX_LENGTH },
      { element: yearInputRef.current, maxLength: YEAR_INPUT_MAX_LENGTH },
    ]);
    const currentInputElementIndex = orderOfInputs.findIndex((el) => el.element === e.currentTarget);
    const currentInputElement = orderOfInputs[currentInputElementIndex];

    if (currentInputElement.maxLength === e.currentTarget.value.length) {
      const nextElement = orderOfInputs[Math.min(currentInputElementIndex + 1, orderOfInputs.length - 1)];
      if (nextElement) {
        nextElement.element?.focus();
      }
    }

    if (e.key === "Backspace" && e.currentTarget.value.length === 0) {
      const prevElement = orderOfInputs[Math.max(currentInputElementIndex - 1, 0)];
      prevElement?.element?.focus();
    }
  };
  const inputs = [
    <DayInput
      key="day"
      placeholder={dayPlaceholder}
      ariaLabel={dayAriaLabel}
      onKeyUp={handleKeyUp}
      ref={dayInputRef}
      type="textInput"
    />,
    <MonthInput
      key="month"
      placeholder={monthPlaceholder}
      ariaLabel={monthAriaLabel}
      onKeyUp={handleKeyUp}
      ref={monthInputRef}
      type="textInput"
    />,
    <YearInput
      minimumAge={minimumAge}
      key="year"
      placeholder={yearPlaceholder}
      ariaLabel={yearAriaLabel}
      onKeyUp={handleKeyUp}
      ref={yearInputRef}
      type="textInput"
    />,
  ] as const;

  if (format === "yyyy") {
    return [inputs[2]];
  }

  if (format === "yyyy/mm/dd") {
    return inputs.slice().reverse();
  }

  return inputs;
};
